import type * as T from "../types";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type OverviewWire = Schemas["OverviewFigures"];
export type AgeingWire = Schemas["AgeingReportResponse"];
export type MovementWire = Schemas["AccountMovement"];

function paisa(value: unknown): number {
  return Math.round(Number(value) * 100);
}

function rupees(amount: number): T.Money {
  return (amount / 100).toFixed(2);
}

/** The upper bound of each bucket names it, and the last is open-ended. */
function bucketLabel(days: readonly number[], index: number): string {
  const upper = days[index];
  if (upper === undefined) return `Over ${days[days.length - 1] ?? 0} days`;
  if (upper === 0) return "Not yet due";
  return `${index === 0 ? 0 : (days[index - 1] ?? 0) + 1}–${upper} days`;
}

export function toAgeing(wire: AgeingWire | null): T.OverviewData["ageing"] {
  if (wire === null) return [];
  const days = wire.bucketDays.map(Number);
  const totals: number[] = [];
  for (const row of wire.rows) {
    row.buckets.forEach((amount, index) => {
      totals[index] = (totals[index] ?? 0) + paisa(amount);
    });
  }
  const whole = totals.reduce((sum, amount) => sum + amount, 0);

  return totals.map((amount, index) => ({
    bucket: bucketLabel(days, index),
    amount: rupees(amount),
    pct: whole === 0 ? 0 : Math.round((amount / whole) * 100),
    tone:
      index === 0 || (index === 1 && days[0] === 0)
        ? ("default" as const)
        : index >= totals.length - 1
          ? ("bad" as const)
          : ("warn" as const),
  }));
}

/**
 * The overview, from the routes that already own each figure. Anything a person may not read arrives
 * as null, and the screen shows less rather than failing whole.
 */
export function toOverview(input: {
  today: T.IsoDate;
  figures: OverviewWire | null;
  banks: ReadonlyArray<{
    id: T.Id;
    name: string;
    bankName?: string | null;
    balance: unknown;
  }> | null;
  ageing: AgeingWire | null;
  recent: T.InvoiceSummary[];
  draftInvoices: number;
  overdueInvoices: number;
  draftBills: number;
}): T.OverviewData {
  const months = (input.figures?.months ?? []).map((month) => ({
    // The chart and its table name a month themselves, from "2026-10".
    month: month.startsOn.slice(0, 7),
    income: rupees(paisa(month.income)),
    expense: rupees(paisa(month.expense)),
  }));
  const net = months.reduce((sum, month) => sum + paisa(month.income) - paisa(month.expense), 0);
  const cash = (input.banks ?? []).reduce((sum, bank) => sum + paisa(bank.balance), 0);

  return {
    asOf: input.today,
    kpis: [
      {
        key: "receivable",
        label: "Owed to you",
        value: rupees(paisa(input.figures?.receivable ?? 0)),
        tone: "default",
      },
      {
        key: "payable",
        label: "You owe",
        value: rupees(paisa(input.figures?.payable ?? 0)),
        tone: "default",
      },
      { key: "cash", label: "Cash and bank", value: rupees(cash), tone: "default" },
      {
        key: "netProfit",
        label: "Net profit, year to date",
        value: rupees(net),
        tone: net < 0 ? "bad" : "ok",
      },
    ],
    queue: [
      {
        key: "draftInvoices",
        title: "Invoices to issue",
        description: "Entered, and waiting for someone else to issue them",
        count: input.draftInvoices,
        icon: "FileText",
        tone: "warn",
        to: "/sales/invoices?status=draft",
      },
      {
        key: "overdue",
        title: "Overdue invoices",
        description: "Past their due date, with money still owed",
        count: input.overdueInvoices,
        icon: "AlarmClock",
        tone: "bad",
        to: "/sales/invoices?payment=overdue",
      },
      {
        key: "draftBills",
        title: "Bills to approve",
        description: "Entered, and waiting for someone else to approve them",
        count: input.draftBills,
        icon: "Receipt",
        tone: "warn",
        to: "/purchases/bills?status=draft",
      },
    ],
    incomeExpense: months,
    netProfitFyToDate: rupees(net),
    recent: input.recent,
    cash: (input.banks ?? []).map((bank) => ({
      bankAccountId: bank.id,
      name: bank.name,
      bank: bank.bankName ?? bank.name,
      balance: rupees(paisa(bank.balance)),
    })),
    ageing: toAgeing(input.ageing),
    bookmarks: [
      {
        label: "Profit and loss",
        period: "This fiscal year",
        icon: "TrendingUp",
        to: "/reports/profit-and-loss",
      },
      {
        label: "Overdue invoices",
        period: "As at today",
        icon: "AlarmClock",
        to: "/sales/invoices?payment=overdue",
      },
    ],
  };
}

function statementRows(
  type: Schemas["AccountType"],
  label: string,
  current: readonly MovementWire[],
  prior: readonly MovementWire[],
  compare: boolean,
): { rows: T.ReportRow[]; total: number; priorTotal: number } {
  const change = (now: number, then: number) =>
    compare && then !== 0 ? Math.round(((now - then) / then) * 1000) / 10 : null;
  const thisYear = current.filter((m) => m.type === type);
  const lastYear = prior.filter((m) => m.type === type);
  const codes = [...new Set([...thisYear, ...lastYear].map((m) => m.code))].sort();
  const sum = (rows: readonly MovementWire[]) => rows.reduce((s, m) => s + paisa(m.amount), 0);

  const lines = codes.map((code): T.ReportRow => {
    const now = paisa(thisYear.find((m) => m.code === code)?.amount ?? 0);
    const then = paisa(lastYear.find((m) => m.code === code)?.amount ?? 0);
    const name = [...thisYear, ...lastYear].find((m) => m.code === code)?.name ?? code;
    return {
      label: name,
      depth: 1,
      kind: "line",
      current: rupees(now),
      compare: compare ? rupees(then) : null,
      deltaPct: change(now, then),
    };
  });
  const total = sum(thisYear);
  const priorTotal = sum(lastYear);

  return {
    total,
    priorTotal,
    rows: [
      { label, depth: 0, kind: "header", current: null, compare: null, deltaPct: null },
      ...lines,
      {
        label: `Total ${label.toLowerCase()}`,
        depth: 0,
        kind: "subtotal",
        current: rupees(total),
        compare: compare ? rupees(priorTotal) : null,
        deltaPct: change(total, priorTotal),
      },
    ],
  };
}

export function toProfitAndLoss(input: {
  period: string;
  compare: string | null;
  current: readonly MovementWire[];
  prior: readonly MovementWire[];
}): T.ProfitAndLoss {
  const compare = input.compare !== null;
  const income = statementRows("income", "Income", input.current, input.prior, compare);
  const expense = statementRows("expense", "Expenses", input.current, input.prior, compare);
  const net = income.total - expense.total;
  const priorNet = income.priorTotal - expense.priorTotal;

  return {
    title: "Profit and loss",
    basis: "accrual",
    period: input.period,
    compare: input.compare,
    untaggedCount: 0,
    rows: [
      ...income.rows,
      ...expense.rows,
      {
        label: "Profit for the year",
        depth: 0,
        kind: "total",
        current: rupees(net),
        compare: compare ? rupees(priorNet) : null,
        deltaPct:
          compare && priorNet !== 0
            ? Math.round(((net - priorNet) / Math.abs(priorNet)) * 1000) / 10
            : null,
      },
    ],
  };
}

/** What awaits approval is the drafts a person may act on; the id says which kind each is. */
export function toApprovalItem(
  kind: "invoice" | "creditNote" | "bill",
  document: { id: T.Id; number: string | null; date: T.IsoDate; total: T.Money },
): T.ApprovalItem {
  return {
    id: `${kind}:${document.id}`,
    documentId: document.id,
    documentNumber: document.number ?? "Draft",
    kind,
    submittedBy: "",
    submittedAt: `${document.date}T00:00:00Z`,
    step: "Approve",
    amount: document.total,
    actionLabel: "Approve",
    canReturn: false,
  };
}
